
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import axiosInstance from '../utils/RequestsQueue';

export const StatsContext = createContext();

export function StatsProvider({ children }) {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  /**
   * Fetch dashboard statistics
   *
   * useCallback ensures the function keeps the same
   * reference between renders.
   */
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);

    try {
      // Always get the latest user from sessionStorage
      const currentUser = JSON.parse(
        sessionStorage.getItem('MazGuardCurrentUser')
      );

      const userId = currentUser?.id;

      const url = `${process.env.REACT_APP_SERVER_URL}/stats/overview${
        userId ? `/${userId}` : ''
      }`;

      const response = await axiosInstance.requestWithQueue({
        url,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      setStats(response?.data?.data ?? null);

      return response?.data?.data;
    } catch (error) {
      console.error('Error fetching statistics:', error);

      return error?.response?.data;
    } finally {
      setStatsLoading(false);
    }
  }, []);

  /**
   * Initial fetch + automatic refresh every 5 minutes
   */
  useEffect(() => {
    // Fetch immediately
    fetchStats();

    // Refresh every 5 minutes
    const intervalId = setInterval(() => {
      fetchStats();
    }, 5 * 60 * 1000);

    // Cleanup
    return () => {
      clearInterval(intervalId);
    };
  }, [fetchStats]);

  /**
   * Memoize context value so consumers don't receive
   * a completely new context object unnecessarily.
   */
  const contextData = useMemo(
    () => ({
      stats,
      setStats,
      fetchStats,
      statsLoading,
      setStatsLoading,
    }),
    [stats, fetchStats, statsLoading]
  );

  return (
    <StatsContext.Provider value={contextData}>
      {children}
    </StatsContext.Provider>
  );
}
